/* tslint:disable:ordered-imports */
import 'isomorphic-fetch';

import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { createUploadLink } from 'apollo-upload-client';
import { hydrateRoot } from 'react-dom/client';

import { GetRequestHeadersQuery } from '@notino/shared/definitions/types';
import { getApolloCacheSettings } from '@notino/shared/graphql/apollo-client-options';

import App from '@containers/App';
import { FeatureFlagsProvider } from '@context/featureFlags/FeatureFlagsProvider';
import { loadDevtools } from '@devtools/dev-tools-load';
import { globalStyleClass } from '@global-styles';
import { createNewRelicBrowserAgent } from '@lib/newRelic/createNewRelicBrowserAgent';
import { NewRelicBrowser } from '@lib/newRelic/NewRelicBrowser';
import { loadableReady } from '@loadable/component';

import getDefaultData from './queries/getDefaultData.graphql';
import getRequestHeadersQuery from './queries/requestHeaders.graphql';
import { initDataLayer, loadApolloState } from './state-load';
import { LocalRequestHeaders } from './utils/admin-panel';

initDataLayer();

const apolloState = loadApolloState();
const cache = new InMemoryCache(getApolloCacheSettings()).restore(apolloState);

// Get default headers from hydrated apollo
const requestHeaders = cache.readQuery<GetRequestHeadersQuery>({
  query: getRequestHeadersQuery,
});

const headers = requestHeaders?.RequestHeaders;
if (!headers) {
  throw new Error('RequestHeaders not found in Apollo cache');
}

NewRelicBrowser.agent = createNewRelicBrowserAgent({
  accountID: headers.newRelicAccountId ?? '',
  applicationID: headers.newRelicAppId ?? '',
  licenseKey: headers.newRelicBrowserKey ?? '',
  environment: headers.newRelicEnvironment ?? '',
});

// Custom attributes for filtering and segmentation in New Relic
NewRelicBrowser.agent.setCustomAttribute('owner', 'discovery');
NewRelicBrowser.agent.setCustomAttribute('shopId', headers.shopId);

// Link browser sessions to semantic version for deployment tracking
if (APP_VERSION) {
  NewRelicBrowser.agent.setApplicationVersion(APP_VERSION);
  NewRelicBrowser.agent.addRelease('product-renderer', APP_VERSION);
}

// Set user ID for user-level error and session tracking
if (headers.userId) {
  NewRelicBrowser.agent.setUserId(headers.userId);
}

// set address for public assets of this app, probably CDN
// eslint-disable-next-line no-undef
// @ts-ignore
__webpack_public_path__ = headers.assetsPath;

LocalRequestHeaders.localHeaders = {
  ...headers,
} as typeof LocalRequestHeaders.localHeaders;

const isGatewayRouterProxyEnabled = Boolean(
  headers?.isGatewayRouterProxyEnabled
);

const auth = setContext((_, { headers: contextHeaders }) => ({
  headers: {
    ...contextHeaders,
    ...LocalRequestHeaders.getHeaders(isGatewayRouterProxyEnabled),
  },
}));

const httpLink = createHttpLink({
  uri: headers.gatewayUrl,
  includeExtensions: true,
});

const persistedQueriesLink = createPersistedQueryLink({
  // @ts-ignore
  generateHash: ({ documentId }) => documentId,
  useGETForHashedQueries: false,
});

const uploadLink = createUploadLink({
  uri: headers.gatewayUrl,
  includeExtensions: true,
});

const client = new ApolloClient({
  connectToDevTools: process.env.NODE_ENV !== 'production',
  link: ApolloLink.from([
    ApolloLink.split(
      (op) =>
        op.query.definitions.some(
          (def) =>
            def.kind === 'OperationDefinition' && def.operation === 'mutation'
        ),
      ApolloLink.from([auth, uploadLink]), // links for mutations
      ApolloLink.from([persistedQueriesLink, auth, httpLink]) // links for queries
    ),
  ]),
  cache,
  ssrMode: false,
  defaultOptions: {
    query: {
      fetchPolicy: 'cache-first',
    },
  },

  resolvers: {
    Query: {
      RequestHeaders: () => {
        return LocalRequestHeaders.localHeaders;
      },
    },
  },
});

client.onResetStore(() => {
  return Promise.resolve(
    cache.writeQuery({
      query: getDefaultData,
      data: {
        RequestHeaders: {
          __typename: 'ClientHeaders',
          ...LocalRequestHeaders.localHeaders,
        },
        // Reseting the pathname because of adminanel
        ProductSsrData: {
          __typename: 'ClientProductSsr',
          id: '0',
          productId: '0',
          type: 'ProductDetail',
          pathname: '',
        },
      },
    })
  );
});

const render = () => {
  const { mounts, originUrl, featureFlags } = headers;

  const scheduleHydration =
    typeof global.setImmediate !== 'undefined' ? setImmediate : (fn) => fn();

  mounts.forEach((mount) =>
    scheduleHydration(() => {
      const mountEl = document.getElementById(mount)!;
      mountEl.classList.add(globalStyleClass);
      hydrateRoot(
        mountEl,
        <ApolloProvider client={client}>
          <BrowserRouter>
            <FeatureFlagsProvider featureFlags={featureFlags}>
              <HelmetProvider>
                <App mountId={mount} originUrl={originUrl ?? undefined} />
              </HelmetProvider>
            </FeatureFlagsProvider>
          </BrowserRouter>
        </ApolloProvider>
      );
    })
  );
};

loadableReady(render).catch((err) => NewRelicBrowser.agent.noticeError(err));

if (
  (headers.featureFlags.pd_should_show_dev_tools &&
    location.hash === '#devtools') ||
  process.env.NODE_ENV === 'development'
) {
  loadDevtools(client, headers.originUrl);
}
